import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import type { AcrScanResult, AcrScanStatus } from "../../types/acr";

/**
 * 単一ファイルの ACR File Scanning API。
 *
 * - `GET  /api/acr-filescanning/:fileId`        単一ファイルの ACR ステータス取得（ポーリング用）
 * - `POST /api/acr-filescanning/:fileId/rescan` 再照合の実行
 *
 * ACR 照合は Celery による非同期処理のため、照合中ファイルは
 * `useGetAcrFileStatusQuery` の `pollingInterval` で完了を検知する
 * （`MediaDetailModal` を参照）。
 */

export interface AcrFileStatusResponse {
  status: string;
  data: {
    file_id: string;
    acr_scan_status: AcrScanStatus;
    acr_scan_result: AcrScanResult | null;
    acr_scanned_at: string | null;
  };
}

export interface RescanFileResponse {
  status: string;
  data: {
    file_id: string;
    acr_scan_status: string;
    acr_scanned_at: string | null;
  };
}

export const acrFilescanningApi = createApi({
  reducerPath: "acrFilescanningApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["AcrFileStatus"],
  endpoints: (builder) => ({
    getAcrFileStatus: builder.query<AcrFileStatusResponse, string>({
      query: (fileId) => `/api/acr-filescanning/${fileId}`,
      providesTags: (_result, _error, fileId) => [
        { type: "AcrFileStatus", id: fileId },
      ],
    }),

    rescanFile: builder.mutation<RescanFileResponse, string>({
      query: (fileId) => ({
        url: `/api/acr-filescanning/${fileId}/rescan`,
        method: "POST",
      }),
      // 再照合後は pending に戻るため、購読中のステータスを即時再取得させる
      invalidatesTags: (_result, _error, fileId) => [
        { type: "AcrFileStatus", id: fileId },
      ],
    }),
  }),
});

export const { useGetAcrFileStatusQuery, useRescanFileMutation } =
  acrFilescanningApi;
